import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";

const REMINDER_CHANNEL_ID = "expolead_lead_reminders";

export async function ensureNotificationChannel() {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await LocalNotifications.createChannel({
      id: REMINDER_CHANNEL_ID,
      name: "Lead Follow-up Reminders",
      description: "Alerts for scheduled visiting card follow-ups and meetings",
      importance: 5, // Heads-up banner + sound
      visibility: 1, // Show on lock screen
      vibration: true,
      lights: true,
      lightColor: "#2563EB",
    });
  } catch (err) {
    console.warn("Notification channel setup notice:", err);
  }
}

export async function requestNotificationPermission() {
  try {
    if (Capacitor.isNativePlatform()) {
      let status = await LocalNotifications.checkPermissions();

      if (status.display !== "granted") {
        status = await LocalNotifications.requestPermissions();
      }

      if (status.display === "granted") {
        await ensureNotificationChannel();
        return true;
      }
      return false;
    }

    // Browser fallback
    if (typeof window !== "undefined" && "Notification" in window) {
      if (Notification.permission === "granted") return true;
      if (Notification.permission !== "denied") {
        const permission = await Notification.requestPermission();
        return permission === "granted";
      }
    }
  } catch (err) {
    console.error("Permission check failed:", err);
  }
  return false;
}

export async function initNotificationService() {
  try {
    const granted = await requestNotificationPermission();
    if (granted && Capacitor.isNativePlatform()) {
      await ensureNotificationChannel();
    }
    return granted;
  } catch (err) {
    console.warn("Notification initialization notice:", err);
    return false;
  }
}

export function getNotificationId(leadId) {
  if (!leadId) return Math.floor(Math.random() * 1000000) + 1;
  const str = String(leadId);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return (Math.abs(hash) % 2147483647) || 1;
}

export async function cancelLeadReminder(leadId) {
  if (!leadId) return;
  const notificationId = getNotificationId(leadId);

  try {
    if (Capacitor.isNativePlatform()) {
      await LocalNotifications.cancel({
        notifications: [{ id: notificationId }],
      });
    }
  } catch (err) {
    console.warn(`Could not cancel notification ${notificationId}:`, err);
  }
}

export async function scheduleLeadReminder({
  leadId,
  name,
  company,
  reminderAt,
  reminder_at,
  notes,
}) {
  const rawDate = reminderAt || reminder_at;
  if (!leadId || !rawDate) return null;

  // Parse directly as local datetime without artificially appending 'Z'
  const scheduledDate = new Date(rawDate);
  const now = new Date();

  // Guard: Reject invalid or past dates
  if (isNaN(scheduledDate.getTime()) || scheduledDate.getTime() <= now.getTime()) {
    return null;
  }

  const notificationId = getNotificationId(leadId);
  await cancelLeadReminder(leadId);

  const contactLabel = name && name.trim() ? name.trim() : "Valued Contact";
  const companyLabel = company && company.trim() ? ` (${company.trim()})` : "";
  const title = `Follow-up: ${contactLabel}`;
  const notesSnippet = notes && notes.trim() ? `\nNote: ${notes.trim().slice(0, 80)}` : "";
  const body = `Scheduled meeting reminder with ${contactLabel}${companyLabel}.${notesSnippet}`;

  try {
    const hasPermission = await requestNotificationPermission();
    if (!hasPermission) return null;

    if (Capacitor.isNativePlatform()) {
      await ensureNotificationChannel();

      await LocalNotifications.schedule({
        notifications: [
          {
            id: notificationId,
            title: title,
            body: body,
            schedule: {
              at: scheduledDate,
              allowWhileIdle: true,
            },
            channelId: REMINDER_CHANNEL_ID,
            smallIcon: "ic_stat_notify",
            iconColor: "#2563EB",
            extra: {
              leadId: String(leadId),
              type: "lead_reminder",
            },
          },
        ],
      });
      return notificationId;
    }
  } catch (err) {
    console.error(`Failed to schedule reminder for lead ${leadId}:`, err);
    return null;
  }

  return notificationId;
}

export async function syncActiveReminders(leadsList = []) {
  if (!Array.isArray(leadsList) || leadsList.length === 0) return;
  if (!Capacitor.isNativePlatform()) return;

  try {
    const hasPermission = await requestNotificationPermission();
    if (!hasPermission) return;

    const pending = await LocalNotifications.getPending();
    const pendingIds = new Set(pending.notifications.map((n) => n.id));
    const now = new Date();

    for (const lead of leadsList) {
      const rawDate = lead.reminder_at || lead.reminderAt;
      if (!rawDate) continue;

      const scheduledDate = new Date(rawDate);
      if (isNaN(scheduledDate.getTime()) || scheduledDate.getTime() <= now.getTime()) continue;

      const notifId = getNotificationId(lead.id || lead._id);
      if (!pendingIds.has(notifId)) {
        await scheduleLeadReminder({
          leadId: lead.id || lead._id,
          name: lead.name,
          company: lead.company,
          reminderAt: scheduledDate,
          notes: lead.notes,
        });
      }
    }
  } catch (err) {
    console.warn("Failed to sync reminders:", err);
  }
}